// hostingProviders.ts — reconnaître, d'après le fournisseur d'une adresse IP, une
// connexion qui ne vient pas d'une box ni d'un forfait mobile (2026-10-17)
//
// Le fournisseur est celui que donne la géolocalisation des connexions (champ `isp`
// d'ip-api, rangé par admin-api). Un hébergeur ou un service de VPN veut dire que la
// personne passe par un VPN, un relais (iCloud Private Relay, Cloudflare WARP) ou un
// serveur : la ville affichée est alors celle du serveur, et une série d'échecs
// depuis une telle adresse ressemble à des essais automatisés de mots de passe.
//
// Liste tenue à la main, en minuscules, comparée par « contient ». Ne pas y mettre
// d'opérateurs grand public (Orange, Free/Proxad, SFR, Bouygues…) : un faux positif
// ferait passer un spectateur ordinaire pour suspect.

export type HostingKind = 'vpn' | 'cloud' | 'relay';

const PROVIDERS: { match: string; kind: HostingKind; label: string }[] = [
  // Services de VPN (et les réseaux qu'ils louent presque entièrement)
  { match: 'm247'                 , kind: 'vpn'  , label: 'M247 (VPN)' },
  { match: 'packethub'            , kind: 'vpn'  , label: 'Packethub (NordVPN)' },
  { match: 'nordvpn'              , kind: 'vpn'  , label: 'NordVPN' },
  { match: 'surfshark'            , kind: 'vpn'  , label: 'Surfshark' },
  { match: 'proton ag'            , kind: 'vpn'  , label: 'Proton VPN' },
  { match: 'mullvad'              , kind: 'vpn'  , label: 'Mullvad' },
  { match: '31173 services'       , kind: 'vpn'  , label: 'Mullvad' },
  { match: 'private internet access', kind: 'vpn', label: 'Private Internet Access' },
  { match: 'datacamp'             , kind: 'vpn'  , label: 'Datacamp (CDN77, VPN)' },
  { match: 'cdn77'                , kind: 'vpn'  , label: 'CDN77 (VPN)' },
  { match: 'clouvider'            , kind: 'vpn'  , label: 'Clouvider (VPN)' },
  { match: 'gsl networks'         , kind: 'vpn'  , label: 'GSL Networks (VPN)' },
  { match: 'tzulo'                , kind: 'vpn'  , label: 'Tzulo (VPN)' },
  { match: 'hostroyale'           , kind: 'vpn'  , label: 'HostRoyale (VPN)' },
  { match: 'performive'           , kind: 'vpn'  , label: 'Performive (VPN)' },
  { match: 'zenlayer'             , kind: 'vpn'  , label: 'Zenlayer' },
  { match: 'psychz'               , kind: 'vpn'  , label: 'Psychz' },
  { match: 'quadranet'            , kind: 'vpn'  , label: 'QuadraNet' },
  { match: 'stark industries'     , kind: 'vpn'  , label: 'Stark Industries' },
  { match: 'ipxo'                 , kind: 'vpn'  , label: 'IPXO' },
  // Hébergeurs et nuages
  { match: 'hetzner'              , kind: 'cloud', label: 'Hetzner' },
  { match: 'ovh'                  , kind: 'cloud', label: 'OVHcloud' },
  { match: 'scaleway'             , kind: 'cloud', label: 'Scaleway' },
  { match: 'online s.a.s'         , kind: 'cloud', label: 'Scaleway (Online SAS)' },
  { match: 'digitalocean'         , kind: 'cloud', label: 'DigitalOcean' },
  { match: 'amazon'               , kind: 'cloud', label: 'Amazon AWS' },
  { match: 'google cloud'         , kind: 'cloud', label: 'Google Cloud' },
  { match: 'microsoft'            , kind: 'cloud', label: 'Microsoft Azure' },
  { match: 'oracle'               , kind: 'cloud', label: 'Oracle Cloud' },
  { match: 'alibaba'              , kind: 'cloud', label: 'Alibaba Cloud' },
  { match: 'tencent'              , kind: 'cloud', label: 'Tencent Cloud' },
  { match: 'linode'               , kind: 'cloud', label: 'Linode (Akamai)' },
  { match: 'vultr'                , kind: 'cloud', label: 'Vultr' },
  { match: 'the constant company' , kind: 'cloud', label: 'Vultr' },
  { match: 'choopa'               , kind: 'cloud', label: 'Vultr (Choopa)' },
  { match: 'contabo'              , kind: 'cloud', label: 'Contabo' },
  { match: 'leaseweb'             , kind: 'cloud', label: 'Leaseweb' },
  { match: 'ionos'                , kind: 'cloud', label: 'IONOS' },
  { match: 'hostinger'            , kind: 'cloud', label: 'Hostinger' },
  { match: 'netcup'               , kind: 'cloud', label: 'netcup' },
  { match: 'aruba s.p.a'          , kind: 'cloud', label: 'Aruba' },
  { match: 'kamatera'             , kind: 'cloud', label: 'Kamatera' },
  { match: 'selectel'             , kind: 'cloud', label: 'Selectel' },
  { match: 'frantech'             , kind: 'cloud', label: 'FranTech (BuyVM)' },
  { match: 'colocrossing'         , kind: 'cloud', label: 'ColoCrossing' },
  // Relais de confidentialité (iCloud Private Relay, Cloudflare WARP)
  { match: 'cloudflare'           , kind: 'relay', label: 'Cloudflare (WARP ou relais)' },
  { match: 'akamai'               , kind: 'relay', label: 'Akamai (relais iCloud)' },
  { match: 'fastly'               , kind: 'relay', label: 'Fastly (relais iCloud)' },
];

export const HOSTING_LABEL: Record<HostingKind, string> = { vpn: 'VPN', cloud: 'HÉBERGEUR', relay: 'RELAIS' };

/** Le fournisseur reconnu (VPN, hébergeur ou relais), ou null pour un opérateur ordinaire. */
export const hostingOf = (isp: string | null | undefined): { kind: HostingKind; label: string } | null => {
  const s = (isp ?? '').toLowerCase();
  if (!s) return null;
  const hit = PROVIDERS.find(p => s.includes(p.match));
  return hit ? { kind: hit.kind, label: hit.label } : null;
};
